/*
 * ==================================================================================================
 * @file useExitProtection.ts
 * @description Registro mínimo de "salida protegida" (SOFY-WU-015). Un flujo con trabajo en curso cuyo
 *              resultado no puede perderse (p. ej. una creación de Reserva pendiente o de resultado
 *              desconocido) registra una función que devuelve el aviso a mostrar, o null si salir es
 *              seguro. Las acciones de sesión compartidas (cerrar sesión desde la cabecera, logout al
 *              cerrar la pestaña) consultan el registro ANTES de destruir la sesión, de modo que cancelar
 *              la salida conserva la sesión. Sin protección registrada todo se comporta como antes.
 * ==================================================================================================
 */

type Protection = () => string | null;

let protection: Protection | null = null;
// Decisión del usuario sobre ESTA salida (una sola interacción puede disparar varias navegaciones:
// el router-link del menú y el router.replace posterior). La limpia quien la tomó.
let decision: boolean | null = null;

/** Registra la protección activa; devuelve la función para retirarla (solo si sigue siendo la misma). */
export function registerExitProtection(fn: Protection): () => void {
  protection = fn;
  return () => {
    if (protection === fn) protection = null;
  };
}

export const exitProtectionMessage = (): string | null =>
  protection?.() ?? null;

export const isExitProtected = (): boolean => exitProtectionMessage() !== null;

/** Decisión ya tomada para la salida en curso (true = confirmada, false = rechazada, null = ninguna). */
export const protectedExitDecision = (): boolean | null => decision;

export const clearProtectedExitDecision = (): void => {
  decision = null;
};

/**
 * Pide confirmación si hay una salida protegida. `false` = el usuario canceló: quien llama NO debe tocar
 * la sesión ni navegar. La decisión queda disponible para que la guarda de la ruta no vuelva a preguntar.
 */
export function requestProtectedExit(
  confirm: (message: string) => boolean = (m) => window.confirm(m),
): boolean {
  const message = exitProtectionMessage();
  if (message === null) return true;
  decision = confirm(message);
  // Rechazada: no habrá más trabajo que la limpie, y puede haber una navegación concurrente (router-link)
  // que aún debe ver la decisión: se retira al terminar la interacción.
  if (!decision) setTimeout(clearProtectedExitDecision, 0);
  return decision;
}

/**
 * Cerrar sesión desde la cabecera: la sesión solo se destruye si la salida está permitida. La navegación
 * posterior pasa por la guarda de la ruta, que reutiliza la decisión (sin preguntar dos veces).
 */
export async function logOutUnlessExitRefused(
  logOut: () => Promise<unknown>,
  navigate: () => Promise<unknown>,
): Promise<boolean> {
  if (!requestProtectedExit()) return false;
  try {
    await logOut();
    await navigate();
  } finally {
    clearProtectedExitDecision();
  }
  return true;
}

/**
 * Salida de página (cierre/recarga). `beforeunload` se dispara ANTES de que el navegador (o el usuario)
 * decida si la página sale de verdad: con una salida protegida no se hace nada irreversible allí y el
 * trabajo se aplaza a `pagehide`, que solo ocurre si la página realmente se descarga. Sin protección
 * el trabajo se ejecuta en `beforeunload`, igual que antes.
 */
export function installPageExitHandlers(run: () => unknown): () => void {
  let deferred = false;
  const onBeforeUnload = () => {
    deferred = isExitProtected();
    if (!deferred) void run();
  };
  const onPageHide = (event: PageTransitionEvent) => {
    if (!deferred) return;
    deferred = false;
    // Solo una descarga real y mientras la protección siga vigente (una página en bfcache o una
    // salida ya cancelada y resuelta no deben cerrar la sesión).
    if (!event.persisted && isExitProtected()) void run();
  };
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("pagehide", onPageHide);
  return () => {
    window.removeEventListener("beforeunload", onBeforeUnload);
    window.removeEventListener("pagehide", onPageHide);
  };
}
